import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import signImage from '../../../../assets/sign.jpg';
import { COMPANY } from '../../../../constants/companyConstants';
import {
    parseSizeFromItemText,
    formatSizeValue,
    removeSizeFromItemText,
    getCurrencySymbol,
    formatDateForPrint,
    formatCurrencyWithSymbol,
    makeCellStyle,
    TH_STYLE,
    computeUniformFontSize,
    computeInvoiceTotals,
} from '../utils/invoicePrintUtils';

const FONT_FAMILY = '"Malgun Gothic", "맑은 고딕", sans-serif';

const TOTALS_CELL_STYLE = {
    padding: '3px 8px',
    fontSize: '10px',
    fontWeight: 'normal',
    borderBottom: '1px solid #e0e0e0',
};

const TOTALS_CELL_LEFT = { ...TOTALS_CELL_STYLE, textAlign: 'left' };
const TOTALS_CELL_RIGHT = { ...TOTALS_CELL_STYLE, textAlign: 'right' };

const TOTAL_ROW_CELL = {
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: 'bold',
    borderTop: '1px solid #000',
    borderBottom: '2px double #000',
};

const TOTAL_ROW_LEFT = { ...TOTAL_ROW_CELL, textAlign: 'left' };
const TOTAL_ROW_RIGHT = { ...TOTAL_ROW_CELL, textAlign: 'right' };

const CurrencyValue = React.memo(({ symbol, value }) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px' }}>
        <span>{symbol}</span>
        <span>{value}</span>
    </div>
));
CurrencyValue.displayName = 'CurrencyValue';

const InvoiceHeader = React.memo(({ invoice, visibleColumns }) => (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 'bold', mb: 0.5, fontFamily: FONT_FAMILY }}>
            {COMPANY.name}
        </Typography>
        <Typography sx={{ fontSize: '11px', fontWeight: 'normal', mb: 0.2, fontFamily: FONT_FAMILY }}>
            {COMPANY.address}
        </Typography>
        <Typography sx={{ fontSize: '11px', fontWeight: 'normal', mb: 1.5, fontFamily: FONT_FAMILY }}>
            {COMPANY.phoneLabel}
        </Typography>
        <Typography sx={{ fontSize: '24px', fontWeight: 'bold', mb: 1, fontFamily: FONT_FAMILY }}>
            INVOICE
        </Typography>
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography sx={{
                    fontWeight: 'bold', fontSize: '11px',
                    visibility: visibleColumns.customerName !== false ? 'visible' : 'hidden',
                    fontFamily: FONT_FAMILY
                }}>
                    CUSTOMER : {invoice.customer_name || '-'}
                </Typography>
                <Typography sx={{
                    fontWeight: 'bold', fontSize: '11px',
                    visibility: visibleColumns.invoiceNo !== false ? 'visible' : 'hidden',
                    fontFamily: FONT_FAMILY
                }}>
                    INVOICE NO : {invoice.invoice_no || '-'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{
                    fontSize: '10px', fontWeight: 'normal',
                    visibility: visibleColumns.customerName !== false ? 'visible' : 'hidden',
                    fontFamily: FONT_FAMILY
                }}>
                    {invoice.customer_address || '-'}
                </Typography>
                <Typography sx={{
                    fontSize: '11px', fontWeight: 'bold',
                    visibility: visibleColumns.invoiceDate !== false ? 'visible' : 'hidden',
                    fontFamily: FONT_FAMILY
                }}>
                    Issued Date : {formatDateForPrint(invoice.invoice_date) || '-'}
                </Typography>
            </Box>
        </Box>
    </Box>
));
InvoiceHeader.displayName = 'InvoiceHeader';

const PRINT_COL_DEFS = [
    { key: 'no', label: 'NO', minCh: 3, visibleKey: null },
    { key: 'buyer', label: 'BUYER', minCh: 8, visibleKey: 'buyer',
      getText: (it) => String(it.buyer || '-') },
    { key: 'styleNo', label: 'STYLE NO', minCh: 10, visibleKey: 'styleNo',
      getText: (it) => String(it.style_no || it.style_name || '-') },
    { key: 'fileNo', label: 'FILE NO', minCh: 10, visibleKey: 'fileNo',
      getText: (it) => String(it.file_no || '-') },
    { key: 'managerName', label: 'MANAGER', minCh: 8, visibleKey: 'managerName',
      getText: (it) => String(it.manager_name || '-') },
    { key: 'factory', label: 'FACTORY', minCh: 8, visibleKey: 'factory',
      getText: (it) => String(it.factory || '-') },
    { key: 'deliveryDate', label: 'DELIVERY DATE', minCh: 11, visibleKey: 'deliveryDate',
      getText: (it) => String(it.delivery_date || it.date || '-') },
    { key: 'item', label: 'ITEM', minCh: 12, visibleKey: 'item',
      getText: (it) => String(it.thread_name || it.item_description || '-').replace(/\s*\[[^\]]*\]\s*/g, '').trim() || '-' },
    { key: 'size', label: 'SIZE', minCh: 6, visibleKey: 'size',
      getText: (it) => {
          const m = String(it.thread_name || it.item_description || '').match(/\[([^\]]+)\]/);
          if (m && m[1]) return formatSizeValue(m[1]);
          return formatSizeValue(it.size_name || it.size) || '-';
      } },
    { key: 'color', label: 'COLOR', minCh: 8, visibleKey: 'color',
      getText: (it) => String(it.color || '-') },
    { key: 'qty', label: 'QTY', minCh: 5, visibleKey: 'qty',
      getText: (it) => it.quantity ? Number(it.quantity).toLocaleString() : '-' },
    { key: 'price', label: 'PRICE', minCh: 9, visibleKey: 'price',
      getText: (it) => it.price || it.unit_price ? String(it.price || it.unit_price) : '-' },
    { key: 'vat', label: 'VAT', minCh: 7, visibleKey: 'vat',
      getText: (it) => {
          const va = Number(it.vat_amount || 0) + Number(it.vat_amount2 || 0);
          return va ? String(va) : '-';
      } },
    { key: 'amount', label: 'AMOUNT', minCh: 11, visibleKey: 'amount',
      getText: (it) => it.amount ? String(it.amount) : '-' }
];

const computeColWidthPercents = (items, visibleColumns) => {
    const visibleCols = PRINT_COL_DEFS.filter((c) =>
        c.visibleKey === null || visibleColumns[c.visibleKey] !== false
    );
    const maxChars = visibleCols.map((col) => {
        let max = col.label.length;
        if (col.getText) {
            for (let i = 0; i < items.length; i++) {
                const t = (col.getText(items[i]) || '').length;
                if (t > max) max = t;
            }
        }
        return Math.max(col.minCh, Math.min(60, max));
    });
    const total = maxChars.reduce((s, n) => s + n, 0) || 1;
    const widthMap = {};
    visibleCols.forEach((col, i) => {
        widthMap[col.key] = `${((maxChars[i] / total) * 100).toFixed(2)}%`;
    });
    return widthMap;
};

const InvoiceItemRow = React.memo(({ item, invoice, index, uniformFs, visibleColumns }) => {
    const v = (val) => String(val || '-');
    const sym = getCurrencySymbol(invoice.currency);
    const vatAmount = Number(item.vat_amount || 0) + Number(item.vat_amount2 || 0);

    const managerText = v(item.manager_name);
    const buyerText = v(item.buyer);
    const styleText = v(item.style_no || item.style_name);
    const fileText = v(item.file_no);
    const factoryText = v(item.factory);
    const dateText = formatDateForPrint(item.delivery_date || item.date) || '-';
    const itemText = removeSizeFromItemText(item.thread_name || item.item_description || '');
    const sizeText = parseSizeFromItemText(item.thread_name || item.item_description || '') || formatSizeValue(item.size_name || item.size) || '-';
    const colorText = v(item.color);
    const qtyText = item.quantity ? Number(item.quantity).toLocaleString() : '-';
    const priceText = (item.price || item.unit_price) ? sym + ' ' + formatCurrencyWithSymbol(item.price || item.unit_price, invoice.currency).value : '-';
    const vatText = vatAmount ? sym + ' ' + formatCurrencyWithSymbol(vatAmount, invoice.currency).value : '-';
    const amountText = item.amount ? sym + ' ' + formatCurrencyWithSymbol(item.amount, invoice.currency).value : '-';

    return (
        <tr>
            <td style={makeCellStyle(uniformFs, String(index + 1))}>{index + 1}</td>
            {visibleColumns.buyer !== false && <td style={makeCellStyle(uniformFs, buyerText)}>{buyerText}</td>}
            {visibleColumns.styleNo !== false && <td style={makeCellStyle(uniformFs, styleText)}>{styleText}</td>}
            {visibleColumns.fileNo !== false && <td style={makeCellStyle(uniformFs, fileText)}>{fileText}</td>}
            {visibleColumns.managerName !== false && <td style={makeCellStyle(uniformFs, managerText)}>{managerText}</td>}
            {visibleColumns.factory !== false && <td style={makeCellStyle(uniformFs, factoryText)}>{factoryText}</td>}
            {visibleColumns.deliveryDate !== false && <td style={makeCellStyle(uniformFs, dateText)}>{dateText}</td>}
            {visibleColumns.item !== false && <td style={makeCellStyle(uniformFs, itemText)}>{itemText}</td>}
            {visibleColumns.size !== false && <td style={makeCellStyle(uniformFs, sizeText)}>{sizeText}</td>}
            {visibleColumns.color !== false && <td style={makeCellStyle(uniformFs, colorText)}>{colorText}</td>}
            {visibleColumns.qty !== false && <td style={makeCellStyle(uniformFs, qtyText)}>{qtyText}</td>}
            {visibleColumns.price !== false && <td style={makeCellStyle(uniformFs, priceText, 'right')}>{priceText}</td>}
            {visibleColumns.vat !== false && <td style={makeCellStyle(uniformFs, vatText, 'right')}>{vatText}</td>}
            {visibleColumns.amount !== false && <td style={makeCellStyle(uniformFs, amountText, 'right')}>{amountText}</td>}
        </tr>
    );
});
InvoiceItemRow.displayName = 'InvoiceItemRow';

const COLOR_TH_STYLE = { ...TH_STYLE, minWidth: '80px' };

const InvoicePrintTemplate = forwardRef(({ selectedInvoices, invoiceData, visibleColumns = {} }, ref) => {
    const [imageUrl, setImageUrl] = useState(signImage);
    const lastLoggedSignatureRef = useRef('');

    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = signImage;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            setImageUrl(canvas.toDataURL('image/jpeg'));
        };

        img.onerror = () => {
            setImageUrl(signImage);
        };
    }, []);

    const groupedInvoiceComputedData = useMemo(() => {
        const groups = [];
        const groupMap = new Map();

        (selectedInvoices || []).forEach((invoiceId) => {
            const invoice = (invoiceData || []).find((item) => item.invoice_id === invoiceId);
            if (!invoice) return;

            const customerKey =
                invoice.customer_id ??
                invoice.vendor_id ??
                invoice.customer_name ??
                invoice.vendor_name ??
                '';
            const managerKey = invoice.manager_id ?? invoice.manager_name ?? '';
            const key = `${customerKey}___${managerKey}`;

            if (!groupMap.has(key)) {
                groupMap.set(key, {
                    groupId: key,
                    baseInvoice: invoice,
                    items: []
                });
                groups.push(groupMap.get(key));
            }

            const group = groupMap.get(key);
            group.items.push(...(invoice.items || []));
        });

        return groups.map((group) => {
            const { baseInvoice, items } = group;
            const totals = computeInvoiceTotals(items);
            const uniformFs = computeUniformFontSize(items, baseInvoice, visibleColumns);
            const colWidths = computeColWidthPercents(items, visibleColumns);
            return {
                groupId: group.groupId,
                invoice: { ...baseInvoice, items },
                items,
                ...totals,
                uniformFs,
                colWidths
            };
        });
    }, [selectedInvoices, invoiceData, visibleColumns]);

    return (
        <Box ref={ref} sx={{ width: '100%', height: '100%', backgroundColor: 'white' }}>
            {groupedInvoiceComputedData.map((computed) => {
                const { groupId, invoice, items, totalQty, subTotal, totalVat, uniformFs, colWidths } = computed;
                const sym = getCurrencySymbol(invoice.currency);

                return (
                    <Box
                        key={groupId}
                        className="invoice-page"
                        sx={{
                            p: '10mm',
                            width: '210mm',
                            minHeight: '297mm',
                            fontFamily: FONT_FAMILY,
                            fontSize: '10px',
                            lineHeight: '1.4',
                            boxSizing: 'border-box',
                            '@media print': {
                                padding: '0 !important',
                                width: '100% !important',
                                minHeight: 'auto !important',
                                pageBreakAfter: 'always',
                                pageBreakInside: 'avoid'
                            }
                        }}
                    >
                        <InvoiceHeader invoice={invoice} visibleColumns={visibleColumns} />

                        <Box sx={{ '@media print': { pageBreakInside: 'avoid' } }}>
                            <table className="main-table" style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                marginBottom: '20px',
                                tableLayout: 'fixed'
                            }}>
                                <colgroup>
                                    <col style={{ width: colWidths.no }} />
                                    {visibleColumns.buyer !== false && <col style={{ width: colWidths.buyer }} />}
                                    {visibleColumns.styleNo !== false && <col style={{ width: colWidths.styleNo }} />}
                                    {visibleColumns.fileNo !== false && <col style={{ width: colWidths.fileNo }} />}
                                    {visibleColumns.managerName !== false && <col style={{ width: colWidths.managerName }} />}
                                    {visibleColumns.factory !== false && <col style={{ width: colWidths.factory }} />}
                                    {visibleColumns.deliveryDate !== false && <col style={{ width: colWidths.deliveryDate }} />}
                                    {visibleColumns.item !== false && <col style={{ width: colWidths.item }} />}
                                    {visibleColumns.size !== false && <col style={{ width: colWidths.size }} />}
                                    {visibleColumns.color !== false && <col style={{ width: colWidths.color }} />}
                                    {visibleColumns.qty !== false && <col style={{ width: colWidths.qty }} />}
                                    {visibleColumns.price !== false && <col style={{ width: colWidths.price }} />}
                                    {visibleColumns.vat !== false && <col style={{ width: colWidths.vat }} />}
                                    {visibleColumns.amount !== false && <col style={{ width: colWidths.amount }} />}
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={TH_STYLE}>NO</th>
                                        {visibleColumns.buyer !== false && <th style={TH_STYLE}>BUYER</th>}
                                        {visibleColumns.styleNo !== false && <th style={TH_STYLE}>STYLE NO</th>}
                                        {visibleColumns.fileNo !== false && <th style={TH_STYLE}>FILE NO</th>}
                                        {visibleColumns.managerName !== false && <th style={TH_STYLE}>MANAGER</th>}
                                        {visibleColumns.factory !== false && <th style={TH_STYLE}>FACTORY</th>}
                                        {visibleColumns.deliveryDate !== false && <th style={TH_STYLE}>DELIVERY DATE</th>}
                                        {visibleColumns.item !== false && <th style={TH_STYLE}>ITEM</th>}
                                        {visibleColumns.size !== false && <th style={TH_STYLE}>SIZE</th>}
                                        {visibleColumns.color !== false && <th style={COLOR_TH_STYLE}>COLOR</th>}
                                        {visibleColumns.qty !== false && <th style={TH_STYLE}>QTY</th>}
                                        {visibleColumns.price !== false && <th style={TH_STYLE}>PRICE</th>}
                                        {visibleColumns.vat !== false && <th style={TH_STYLE}>VAT</th>}
                                        {visibleColumns.amount !== false && <th style={TH_STYLE}>AMOUNT</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, itemIndex) => (
                                        <InvoiceItemRow
                                            key={itemIndex}
                                            item={item}
                                            invoice={invoice}
                                            index={itemIndex}
                                            uniformFs={uniformFs}
                                            visibleColumns={visibleColumns}
                                        />
                                    ))}
                                </tbody>
                            </table>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 2,
                                gap: 4,
                                '@media print': {
                                    display: 'flex !important',
                                    justifyContent: 'space-between !important',
                                    marginTop: '16px !important',
                                    gap: '32px !important',
                                    pageBreakInside: 'avoid !important',
                                    breakInside: 'avoid !important'
                                }
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold', mb: 0.2, fontFamily: FONT_FAMILY }}>At bank : {COMPANY.bank.name}</Typography>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold', mb: 0.2, fontFamily: FONT_FAMILY }}>Bank Acc ($) : {COMPANY.bank.accountUSD} SWIFT CODE : {COMPANY.bank.swiftCode}</Typography>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold', mb: 0.2, fontFamily: FONT_FAMILY }}>Bank Acc (Rp) : {COMPANY.bank.accountIDR} SWIFT CODE : {COMPANY.bank.swiftCode}</Typography>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold', fontFamily: FONT_FAMILY }}>ACC NAME : {COMPANY.bank.accountName}</Typography>
                                </Box>
                                <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                                    <table className="totals-table" style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontFamily: FONT_FAMILY
                                    }}>
                                        <tbody>
                                            {visibleColumns.qty !== false && (
                                                <tr>
                                                    <td style={TOTALS_CELL_LEFT}>TOTAL Q'TY</td>
                                                    <td style={TOTALS_CELL_RIGHT}>{totalQty.toLocaleString()}</td>
                                                </tr>
                                            )}
                                            {visibleColumns.amount !== false && (
                                                <tr>
                                                    <td style={TOTALS_CELL_LEFT}>SUB TOTAL</td>
                                                    <td style={TOTALS_CELL_RIGHT}>
                                                        <CurrencyValue symbol={sym} value={formatCurrencyWithSymbol(subTotal, invoice.currency).value} />
                                                    </td>
                                                </tr>
                                            )}
                                            {visibleColumns.vat !== false && (
                                                <tr>
                                                    <td style={TOTALS_CELL_LEFT}>VAT</td>
                                                    <td style={TOTALS_CELL_RIGHT}>
                                                        <CurrencyValue symbol={sym} value={formatCurrencyWithSymbol(totalVat, invoice.currency).value} />
                                                    </td>
                                                </tr>
                                            )}
                                            {visibleColumns.amount !== false && (
                                                <tr>
                                                    <td style={TOTAL_ROW_LEFT}>TOTAL</td>
                                                    <td style={TOTAL_ROW_RIGHT}>
                                                        <CurrencyValue symbol={sym} value={formatCurrencyWithSymbol(subTotal + totalVat, invoice.currency).value} />
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {visibleColumns.sign !== false && (
                                        <Box className="signature-box" sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                            <img
                                                src={imageUrl}
                                                alt="Signature"
                                                loading="eager"
                                                style={{
                                                    height: '80px',
                                                    width: 'auto',
                                                    objectFit: 'contain',
                                                    marginLeft: '20px',
                                                    imageRendering: 'auto'
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
});

InvoicePrintTemplate.displayName = 'InvoicePrintTemplate';

export default InvoicePrintTemplate;
